import { MapComponent, MapType } from '../map/map.component';
import { PigeonsEppComponent } from './pigeons-epp/pigeons-epp.component';
import { PigeonsLegendComponent } from './pigeons-legend/pigeons-legend.component';
import { PigeonsMatrixComponent } from './pigeons-matrix.component';
import { PigeonsSettingsComponent } from './pigeons-settings/pigeons-settings.component';
import { PigeonsVideoComponent } from './pigeons-video/pigeons-video.component';

import { AuthService } from '../auth/auth.service';
import { EppService } from '../epp/epp.service';
import { RadarDtg } from '../radar/radar.service';
import { LanguageService } from '../shared/services/language.service';
import { SmwService } from '../smw/smw.service';
import { PigeonsService } from './pigeons.service';

import { IsNilPipe } from '../pipes/is-nil.pipe';
import { DatexPipe } from '../shared/datex.pipe';
import { KeyPipe } from '../shared/key.pipe';
import { RoundPipe } from '../shared/round.pipe';
import { DtgOfSelectedTimestepPipe } from './pipes/dtg-of-selected-timestep.pipe';
import { RouteEndHourPipe } from './pipes/route-end-hour.pipe';
import { RouteStartHourPipe } from './pipes/route-start-hour.pipe';
import { StationNamePipe } from './pipes/station-name.pipe';
import { SuntimesPipe } from './pipes/suntimes.pipe';

import { WmIconDirective } from '../shared/wm-icon.directive';

import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { CommonModule, ViewportScroller } from '@angular/common';
import {
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  Injector,
  model,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {
  FormControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatLineModule } from '@angular/material/core';
import {
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import {
  MatDrawer,
  MatDrawerContainer,
  MatSidenavModule,
} from '@angular/material/sidenav';
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import {
  dtgOfSelectedTimestep,
  parameterSettings,
} from './constants/constants';
import { CollectionOfPigeonMosData } from './models/collection-of-pigeon-mos-data.interface';
import { FilteredStations } from './models/filtered-stations.interface';
import { PigeonRoute } from './models/pigeon-route.interface';
import { SinglePigeonMosData } from './models/single-pigeon-mos-data.interface';
import { TippyDirective } from '@ngneat/helipopper';
import { LngLat, LngLatLike } from 'mapbox-gl';
import { Moment } from 'moment';
import moment from 'moment';
import rg4js from 'raygun4js';
import { Observable, Subscription, timer } from 'rxjs';
import { debounceTime, finalize, map, switchMap, tap } from 'rxjs/operators';
import { isNil } from 'src/utils/utils';

export const MODULES = [
  CommonModule,
  RouterModule,
  MatSidenavModule,
  ReactiveFormsModule,
  MatFormFieldModule,
  MatInputModule,
  MatAutocompleteModule,
  MatProgressSpinnerModule,
  MatButtonModule,
  MatSelectModule,
  MatCardModule,
  MatSlideToggleModule,
  MatDatepickerModule,
  MatTabsModule,
  MatTooltipModule,
  MatIconModule,
  MatDividerModule,
  MatListModule,
  TranslateModule,
  MatLineModule,
];
export const COMPONENTS = [
  MatDrawerContainer,
  PigeonsSettingsComponent,
  PigeonsVideoComponent,
  FaIconComponent,
  PigeonsMatrixComponent,
  MapComponent,
  PigeonsEppComponent,
  PigeonsLegendComponent,
];
export const PIPES = [
  DatexPipe,
  RoundPipe,
  KeyPipe,
  IsNilPipe,
  DtgOfSelectedTimestepPipe,
  RouteStartHourPipe,
  RouteEndHourPipe,
  StationNamePipe,
  SuntimesPipe,
];
export const DIRECTIVES = [
  TippyDirective,
  WmIconDirective,
  CdkDropList,
  CdkDrag,
];

type SettingsParameterEntry = { parameter: string; selected: boolean };
type SettingsParametersBySet = Record<string, SettingsParameterEntry[]>;

@Component({
  selector: 'app-pigeons',
  templateUrl: './pigeons.component.html',
  styleUrls: ['./pigeons.component.scss', './print-pigeons.component.scss'],
  imports: [...MODULES, ...COMPONENTS, ...PIPES, ...DIRECTIVES],
})
export class PigeonsComponent implements OnInit, OnDestroy {
  private injector = inject(Injector);
  private fb = inject(UntypedFormBuilder);
  public pigeonsService = inject(PigeonsService);
  private viewportScroller = inject(ViewportScroller);
  public auth = inject(AuthService);
  private renderer = inject(Renderer2);
  private eppService = inject(EppService);
  private smwService = inject(SmwService);
  public languageService = inject(LanguageService);

  pigeonsEppComponent = viewChild<PigeonsEppComponent>(PigeonsEppComponent);
  drawer = viewChild<MatDrawer>('drawer');

  parameters = parameterSettings;
  selectedParameter = signal<string>(undefined);
  routes = signal([]);
  selectedRoute = model<number>();
  pigeonRoute = signal<PigeonRoute>(undefined);
  routeStartFlag = 'flag-0';

  mapType: typeof MapType = MapType;

  showMoreDays = signal(false);
  daysCount = 5;
  startHour = 5;
  endHour = 21;
  pigeonSpeed = model(70);
  pigeonSpeedLimits = [60, 70, 80, 90, 100, 110, 120];
  showValuesOnMap = signal(true);
  isMasterUser = signal(false);

  mapsTime = signal<Moment>(undefined);
  days = signal<Array<Moment>>(undefined);
  selectedDay = signal<Moment>(null);
  sliderDtgs: Array<Moment>;
  dtgIndex = signal<number>(undefined);
  dtgs = signal<Array<RadarDtg>>([]);
  allDtgs = {};

  timeSteps = signal([]);
  selectedTime = signal<number>(undefined);
  stationTime = signal({});
  stationIndexTime = signal({});

  selectedStation = model<number>(undefined);
  selectedStationIndex = signal<number>(undefined);

  pigeonRouteDetails = signal({
    start: new LngLat(0, 0),
    end: new LngLat(0, 0),
    pos: new LngLat(0, 0),
    distance: 0,
    duration: 0,
    points: [],
    stations: [],
    geoJsonBase: null,
    geoJsonMOS: [],
    geoJsonFlags: [],
  });
  pigeonMos = signal({});
  routePosition = signal({});

  individualRouteForm: UntypedFormGroup;
  startStations$: Observable<any[]>;
  endStations$: Observable<any[]>;
  isStationSearchLoading = signal(false);
  useStandardRoute = true;
  startChangeFromMap = false;

  showTutorialVideo = signal(false);

  hideSettings = signal(true);
  outOfTimeScope = signal(false);
  neutralColor = signal('green');
  showTableSettings = signal(false);
  settingsParameterset = signal<string>(undefined);
  settingsParameterTable = signal<SettingsParameterEntry[]>([]);
  settingsParameters = signal<SettingsParameterEntry[]>([]);
  map = viewChild<MapComponent>(MapComponent);
  placeholderImageForPigeonMap = viewChild<ElementRef>(
    'placeholderImageForPigeonMap'
  );
  eppForecastDtg = signal<Moment>(undefined);
  sunTimesArray = signal({});
  subscriptions: Subscription[] = [];
  lastRefreshHour = 0;

  showArchive = signal(false);
  selectedArchiveDate = signal<Moment | null>(null);
  archiveDateControl = new FormControl<Moment>(moment().startOf('day'));
  minArchiveDate = moment().set('year', 2026).set('month', 3).set('date', 6);
  maxArchiveDate = moment().subtract(1, 'day');
  mapsArchiveCountMin = 0;
  mapsArchiveCountMax = 23;

  constructor() {
    this.listenToFillSunTimesArray();
  }

  ngOnInit(): void {
    this.setMasterUserFlag();

    this.extractRoutesFromProducts();
    this.initIndividualRouteSearch();
    this.selectedParameter.set(Object.keys(this.parameters)[0]);
    this.setDtgs();
    this.setTimeSteps();

    setTimeout(() => {
      this.loadSession();
      this.neutralColor.set(this.eppService.readColorScheme());
      this.pigeonsService.readEppSettings();
      this.pigeonsService.initParameters();
      this.syncSettingsParameters();
      this.setRouteStartFlag();
      this.autoRefreshPigeonData();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  onSetupSettings(): void {
    this.syncSettingsParameters();
    this.drawer()?.toggle();
  }

  private syncSettingsParameters(): void {
    const parametersBySet = this.pigeonsService
      .userParametersets as SettingsParametersBySet;
    const currentSet = this.pigeonsService.parameterset ?? 'pigeon';
    this.settingsParameterset.set(currentSet);

    const source =
      parametersBySet?.[currentSet] ??
      this.pigeonsService.toUserParameterset(
        this.pigeonsService.defaultParametersetDetails?.[currentSet] ?? []
      ) ??
      [];

    this.settingsParameters.set([...source]);
  }

  onRestoreStandardSettings(): void {
    this.pigeonsService.readEppSettings();
    this.pigeonsService.initParameters();
    this.syncSettingsParameters();
  }

  onSaveSettings(): void {
    const parametersBySet = this.pigeonsService
      .userParametersets as SettingsParametersBySet;
    const currentSet = this.pigeonsService.parameterset ?? 'pigeon';

    parametersBySet[currentSet] = [...this.settingsParameters()];
    this.pigeonsService.saveTableSettings(
      this.settingsParameters(),
      this.neutralColor()
    );

    this.drawer()?.toggle();
  }

  toggleAllParameters(checked: boolean): void {
    this.settingsParameters.update(list =>
      list.map(entry => ({ ...entry, selected: checked }))
    );
  }

  onParameterSelectionChange(event: MatSelectionListChange): void {
    const { value, selected } = event.options[0];

    this.settingsParameters.update(list =>
      list.map(p => (p.parameter === value ? { ...p, selected } : p))
    );
  }

  getVisiblePigeonParameters(): string[] {
    return this.settingsParameters()
      .filter(entry => entry.selected)
      .map(entry => entry.parameter);
  }

  getSettingsParameters(): Array<SettingsParameterEntry> {
    return this.settingsParameters();
  }

  onRefreshEppData() {
    this.pigeonsEppComponent()?.onRefreshEppData();
  }

  private setMasterUserFlag(): void {
    if (this.auth.authenticated()) {
      const userCustomerDocument = JSON.parse(
        localStorage.getItem('user_customer_document')
      );
      if (userCustomerDocument !== null) {
        this.isMasterUser.set(userCustomerDocument['isMaster']);
        if (this.isMasterUser()) {
          this.showMoreDays.set(true);
          this.daysCount = 6;
        }
      }
    }
  }

  private autoRefreshPigeonData() {
    this.lastRefreshHour = moment().hour();
    const autoRefreshPigeonDataSubscription = timer(0, 30000).subscribe(() => {
      const currentHour = moment().hour();
      if (currentHour !== this.lastRefreshHour) {
        this.setDtgs();
        this.setTimeSteps();
        this.loadSession();
        this.setRouteStartFlag();
        this.lastRefreshHour = currentHour;
      }
    });
    this.subscriptions.push(autoRefreshPigeonDataSubscription);
  }

  onPrint(): void {
    this.onBeforePrint();
    setTimeout(() => {
      window.print();
    }, 2000);
  }

  @HostListener('window:beforeprint', ['$event'])
  onBeforePrint(): void {
    const canvasMap = this.map().map.getCanvas();
    const canvasAsImage = canvasMap.toDataURL('image/png', 1);
    this.renderer.setAttribute(
      this.placeholderImageForPigeonMap().nativeElement,
      'src',
      canvasAsImage
    );
  }

  onSwitchShowMoreDays() {
    this.showMoreDays.update(showMoreDays => !showMoreDays);
    this.daysCount = this.showMoreDays() ? 6 : 5;
    this.setDtgs();
    this.setTimeSteps();
  }

  extractRoutesFromProducts(): void {
    const customerDocument = this.auth.getCustomerDocument();
    const routes = [];
    if (
      customerDocument.hasOwnProperty('pigeonProducts') &&
      customerDocument['pigeonProducts'].length > 0
    ) {
      for (const product of customerDocument['pigeonProducts']) {
        if (this.auth.isProductActive('pigeonProducts', product.id)) {
          for (const route of product['routes']) {
            routes.push(route);
          }
        }
      }
    }
    this.routes.set([...routes.sort((a, b) => (a.name > b.name ? 1 : -1))]);
  }

  setDtgs(): void {
    const baseTime = this.showArchive()
      ? moment(this.selectedArchiveDate())
      : moment();

    this.mapsTime.set(
      moment(baseTime)
        .set({
          hour: this.showArchive() ? this.startHour : moment().hour(),
          minute: 0,
          second: 0,
          millisecond: 0,
        })
        .clone()
    );

    this.days.set([]);
    for (let i = 0; i < this.daysCount; i += 1) {
      this.days.update(days => {
        days.push(moment(this.mapsTime()).add(i, 'day'));
        return [...days];
      });
    }

    this.sliderDtgs = [];
    this.dtgs.set([]);
    this.allDtgs = {};

    // Always start with a real day, never with null.
    const initialDay = this.days()?.[0] ?? null;
    this.selectedDay.set(initialDay);
    this.pigeonsService.day.set(initialDay);

    this.dtgIndex.set(0);

    for (let day = 0; day < this.daysCount; day++) {
      const dayKey = this.days()[day].unix();
      this.allDtgs[dayKey] = [];

      for (let i = 0; i <= this.endHour - this.startHour; i++) {
        const dtg = moment(this.mapsTime()).add(day, 'days').add(i, 'hour');
        const timestep = dtg.unix();

        const mapDtg: RadarDtg = {
          mapServer: moment(dtg).utc().format('YYYY-MM-DD HH:mm'),
          slider: timestep,
          info: dtg.format('dd DD.MM.YYYY HH:mm'),
          visible: timestep === 0,
          isFcst: false,
          isArchive: false,
          issue: 0,
        };

        this.allDtgs[dayKey].push(mapDtg);

        if (initialDay && initialDay.isSame(this.days()[day])) {
          this.dtgs.update(dtgs => [...dtgs, mapDtg]);
          this.sliderDtgs.push(dtg);
        }
      }
    }

    if (this.dtgs().length > 0) {
      this.mapsTime.set(this.sliderDtgs[this.dtgIndex()]?.clone());
    }
  }

  onSelectDay(day?: Moment): void {
    if (isNil(day)) {
      return;
    }

    this.selectedDay.set(day);
    this.pigeonsService.day.set(day);
    this.setTimeSteps();

    this.dtgs.set([...(this.allDtgs[day.unix()] ?? [])]);

    this.sliderDtgs = [];
    for (const dtg of this.dtgs()) {
      this.sliderDtgs.push(moment.unix(dtg.slider));
    }

    if (this.sliderDtgs.length > 0) {
      this.dtgIndex.set(0);
      this.mapsTime.set(this.sliderDtgs[0]?.clone());
    }

    this.setStationsTime(this.selectedStationIndex());
    this.routePosition.update(routePosition => {
      return {
        ...routePosition,
        ['color']: this.pigeonsService.pigeonColor(
          this.parameters[this.selectedParameter()].name,
          routePosition['station'],
          this.pigeonMos(),
          this.dtgs(),
          this.dtgIndex(),
          this.selectedTime() - this.startHour
        ),
      };
    });
    this.setRouteStartFlag();
  }

  onSetShowArchive(event: MatSlideToggleChange) {
    this.showArchive.set(event.checked);

    if (!this.showArchive()) {
      this.clearSessionStorage('pigeons-mos');
      this.archiveDateControl.setValue(moment().startOf('day'));
      this.selectedArchiveDate.set(null);
      this.setDtgs();
      this.onSelectDay(this.days()?.[0]);
      this.getNewMosData(null);
    }
  }

  // hours for matrix
  setTimeSteps(): void {
    const timeSteps = [];
    let startHour =
      this.selectedDay() === null
        ? moment().subtract(2, 'hours').hour()
        : this.startHour;
    if (
      this.showArchive() &&
      (this.selectedDay() === null ||
        this.selectedDay().day() === this.selectedArchiveDate().day())
    ) {
      startHour = this.startHour;
    }

    const endHour =
      this.selectedDay() === null ? moment().hour() : this.endHour;
    for (let i = startHour; i <= endHour; i++) {
      timeSteps.push(i);
    }
    this.timeSteps.set([...timeSteps]);
    if (this.selectedDay() === null) {
      this.selectedTime.set(endHour);
    } else {
      if (!this.selectedTime()) {
        this.selectedTime.set(this.startHour + 3);
      }
    }
  }

  selectStandardRoute(route: number): void {
    this.pigeonRoute.set({ ...this.routes()[route] });
    this.getPigeonRouteDetails();
  }

  newPigeonRoute(): any {
    return {
      start: new LngLat(0, 0),
      end: new LngLat(0, 0),
      pos: new LngLat(0, 0),
      distance: 0,
      duration: 0,
      points: [],
      stations: [],
      geoJsonBase: null,
      geoJsonMOS: [],
      geoJsonFlags: [],
    };
  }

  setGeoJsonFlags(pigeonRouteDetails): Array<any> {
    const geoJsonFlags = [];
    geoJsonFlags.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [
          pigeonRouteDetails.start.lng,
          pigeonRouteDetails.start.lat,
        ],
      },
      properties: {
        icon: this.routeStartFlag,
      },
    });
    geoJsonFlags.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [pigeonRouteDetails.end.lng, pigeonRouteDetails.end.lat],
      },
      properties: {
        icon: 'flag-end',
      },
    });

    return geoJsonFlags;
  }

  async createPigeonRoute(startPoint, endPoint): Promise<void> {
    this.clearSessionStorage('pigeons-mos');
    const pigeonRouteDetails = this.newPigeonRoute();
    this.pigeonRouteDetails.set({ ...pigeonRouteDetails });
    // draw route as a red line in the background
    pigeonRouteDetails.start = new LngLat(
      Number(startPoint.longitude),
      Number(startPoint.latitude)
    );
    pigeonRouteDetails.end = new LngLat(
      Number(endPoint.longitude),
      Number(endPoint.latitude)
    );
    pigeonRouteDetails.distance = Math.round(
      pigeonRouteDetails.start.distanceTo(pigeonRouteDetails.end) / 1000
    );
    pigeonRouteDetails.duration = Math.round(
      pigeonRouteDetails.start.distanceTo(pigeonRouteDetails.end) /
        1000 /
        this.pigeonSpeed()
    );
    if (this.selectedRoute() < 9999) {
      this.setDefaultRouteStationToFormFields(startPoint.name, endPoint.name);
    }
    pigeonRouteDetails.geoJsonBase = {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [
          [pigeonRouteDetails.start.lng, pigeonRouteDetails.start.lat],
          [pigeonRouteDetails.end.lng, pigeonRouteDetails.end.lat],
        ],
      },
    };
    pigeonRouteDetails.geoJsonFlags = this.setGeoJsonFlags(pigeonRouteDetails);

    // draw colored route based on MOS data
    // const pigeonSpeed = 70;
    // const runningTime = Math.round(pigeonRouteDetails.start.distanceTo(pigeonRouteDetails.end) / 1000 / pigeonSpeed);
    const latDiff =
      pigeonRouteDetails.duration > 0
        ? (pigeonRouteDetails.end.lat - pigeonRouteDetails.start.lat) /
          pigeonRouteDetails.duration
        : 1;
    const lonDiff =
      pigeonRouteDetails.duration > 0
        ? (pigeonRouteDetails.end.lng - pigeonRouteDetails.start.lng) /
          pigeonRouteDetails.duration
        : 1;
    for (let i = 0; i <= pigeonRouteDetails.duration; i++) {
      // ERROR MONITORIZATION
      try {
        const point = new LngLat(
          pigeonRouteDetails.start.lng + i * lonDiff,
          pigeonRouteDetails.start.lat + i * latDiff
        );
        const prevPoint =
          i === 0
            ? pigeonRouteDetails.start
            : pigeonRouteDetails.points[i - 1].to;
        pigeonRouteDetails.points.push({ from: prevPoint, to: point });
        pigeonRouteDetails.geoJsonMOS.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [prevPoint.lng, prevPoint.lat],
              [point.lng, point.lat],
            ],
          },
        });
        const prevStationToCompare =
          i > 0 && i < pigeonRouteDetails.duration
            ? pigeonRouteDetails.stations[i - 1]
            : null;
        const station = await this.pigeonsService.nearestStationofPosition(
          point,
          prevStationToCompare
        );
        pigeonRouteDetails.stations.push(station);
        const mosData = await this.pigeonsService.mosDataOfStation(
          station.stationId,
          this.selectedArchiveDate()
        );
        this.pigeonMos.update(pigeonMos => {
          return { ...pigeonMos, [station.stationId]: mosData };
        });

        if (i === 0) {
          this.selectedStation.set(station.stationId);
          this.selectedStationIndex.set(0);
          this.routePosition.set({
            station: this.selectedStation(),
            lnglat: [
              pigeonRouteDetails.points[0].to.lng,
              pigeonRouteDetails.points[0].to.lat,
            ],
            color: this.pigeonsService.pigeonColor(
              this.parameters[this.selectedParameter()].name,
              this.selectedStation(),
              this.pigeonMos(),
              this.dtgs(),
              this.dtgIndex(),
              this.selectedTime() - this.startHour
            ),
          });
        }
      } catch (error) {
        rg4js('send', {
          error,
          customData: {
            pigeonRouteDetails: JSON.stringify(pigeonRouteDetails),
            startPoint,
            endPoint,
          },
        });
        throw error;
      }
    }
    if (this.selectedRoute() < 9999) {
      this.pigeonsService.savePigeonRoute(
        this.pigeonRoute().id,
        this.pigeonSpeed(),
        pigeonRouteDetails
      );
    }
    this.pigeonRouteDetails.set({ ...pigeonRouteDetails });
    this.saveSession();
    this.setStationsTime(0);
  }

  getPigeonRouteFromCache(storedRoute): void {
    this.pigeonRouteDetails.set({ ...storedRoute });
    this.selectedStation.set(this.pigeonRouteDetails().stations[0].stationId);
    this.selectedStationIndex.set(0);
    this.setStationsTime(0);
    this.setDefaultRouteStationToFormFields(
      this.pigeonRouteDetails().stations[0].name,
      this.pigeonRouteDetails().stations[
        this.pigeonRouteDetails().stations.length - 1
      ].name
    );
    for (const station of this.pigeonRouteDetails().stations) {
      this.pigeonsService
        .mosDataOfStation(station.stationId, this.selectedArchiveDate())
        .then(mosData => {
          if (mosData) {
            this.pigeonMos.update(pigeonMos => {
              return { ...pigeonMos, [station.stationId]: mosData };
            });
            if (station.stationId === this.selectedStation()) {
              this.routePosition.set({
                station: this.selectedStation(),
                lnglat: [
                  this.pigeonRouteDetails().points[0].to.lng,
                  this.pigeonRouteDetails().points[0].to.lat,
                ],
                color: this.pigeonsService.pigeonColor(
                  this.parameters[this.selectedParameter()].name,
                  this.selectedStation(),
                  this.pigeonMos(),
                  this.dtgs(),
                  this.dtgIndex(),
                  this.selectedTime() - this.startHour
                ),
              });
            }
          }
        });
    }
  }

  async getPigeonRouteDetails(modelRun: Moment | null = null): Promise<void> {
    if (!this.pigeonRoute()) {
      return;
    }
    const storedRoute = this.pigeonsService.restorePigeonRoute(
      this.pigeonRoute().id,
      this.pigeonSpeed()
    );
    if (storedRoute) {
      this.getPigeonRouteFromCache(storedRoute);
      this.saveSession();
    } else {
      const startPoint = await this.pigeonsService.getStationInformation(
        this.pigeonRoute().stations.start
      );
      const endPoint = await this.pigeonsService.getStationInformation(
        this.pigeonRoute().stations.end
      );
      await this.createPigeonRoute(startPoint, endPoint);
      this.saveSession();
    }
  }

  setDefaultRouteStationToFormFields(
    startStationName: string,
    endStationName: string
  ): void {
    this.useStandardRoute = true;
    this.individualRouteForm.controls['individualRouteStart'].patchValue(
      startStationName,
      { onlySelf: true, emitEvent: false }
    );
    this.individualRouteForm.controls['individualRouteEnd'].patchValue(
      endStationName,
      { onlySelf: true, emitEvent: false }
    );
  }

  stationPositionOfRoute(stationId: number): number {
    for (let i = 0; i < this.pigeonRouteDetails().stations.length; i++) {
      if (this.pigeonRouteDetails().stations[i].stationId === stationId) {
        return i;
      }
    }
    return 0;
  }

  dtgOfSelectedTimeStep(): Moment {
    return dtgOfSelectedTimestep(this.selectedTime(), this.mapsTime());
  }

  clearSessionStorage(prefix: string): void {
    const toRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key.indexOf(prefix) >= 0) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      sessionStorage.removeItem(key);
    }
  }

  onChangeSpeed(event: MatSelectChange): void {
    this.clearSessionStorage('pigeons-mos');
    this.clearSessionStorage('pigeons-route');
    this.routePosition.set({});
    this.pigeonMos.set({});
    this.pigeonsService.mosData.set({});
    if (this.selectedRoute() < 9999) {
      this.pigeonRouteDetails.set({ ...this.newPigeonRoute() });
      this.pigeonRoute.set({ ...this.routes()[this.selectedRoute()] });
      setTimeout(() => {
        this.getPigeonRouteDetails();
      }, 200);
    } else {
      const start = {
        name: this.individualRouteForm.controls['individualRouteStart'].value,
        longitude: this.pigeonRouteDetails().start.lng,
        latitude: this.pigeonRouteDetails().start.lat,
      };
      const end = {
        name: this.individualRouteForm.controls['individualRouteEnd'].value,
        longitude: this.pigeonRouteDetails().end.lng,
        latitude: this.pigeonRouteDetails().end.lat,
      };
      this.pigeonRouteDetails.set({ ...this.newPigeonRoute() });
      this.createPigeonRoute(start, end);
    }
  }

  onReverseRoute(): void {
    this.pigeonRouteDetails.update(pigeonRouteDetails => {
      pigeonRouteDetails.geoJsonBase = null;
      return { ...pigeonRouteDetails };
    });
    setTimeout(() => {
      const start = this.pigeonRouteDetails().end;
      const end = this.pigeonRouteDetails().start;
      const distance = this.pigeonRouteDetails().distance;
      const duration = this.pigeonRouteDetails().duration;
      const points = [...this.pigeonRouteDetails().points].reverse();
      const stations = [...this.pigeonRouteDetails().stations].reverse();

      const startStationName =
        this.individualRouteForm.controls['individualRouteEnd'].value;
      const endStationName =
        this.individualRouteForm.controls['individualRouteStart'].value;

      this.individualRouteForm.controls['individualRouteStart'].patchValue(
        startStationName,
        { onlySelf: true, emitEvent: false }
      );
      this.individualRouteForm.controls['individualRouteEnd'].patchValue(
        endStationName,
        { onlySelf: true, emitEvent: false }
      );
      this.selectedRoute.set(9999);

      this.pigeonRouteDetails.set({
        ...this.newPigeonRoute(),
        start: start,
        end: end,
        points: points,
        stations: stations,
        distance: distance,
        duration: duration,
      });

      const geoJsonMOS = [];
      for (const point of points) {
        geoJsonMOS.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [point.from.lng, point.from.lat],
              [point.to.lng, point.to.lat],
            ],
          },
        });
      }
      this.pigeonRouteDetails.update(pigeonRouteDetails => {
        pigeonRouteDetails.geoJsonMOS = geoJsonMOS;
        pigeonRouteDetails.geoJsonBase = {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [start.lng, start.lat],
              [end.lng, end.lat],
            ],
          },
        };
        pigeonRouteDetails.geoJsonFlags =
          this.setGeoJsonFlags(pigeonRouteDetails);
        return { ...pigeonRouteDetails };
      });

      this.selectedStationIndex.set(0);
      this.selectedStation.set(stations[0].stationId);
      this.setStationsTime(0);

      this.routePosition.set({
        station: this.selectedStation(),
        lnglat: [points[0].to.lng, points[0].to.lat],
        color: this.pigeonsService.pigeonColor(
          this.parameters[this.selectedParameter()].name,
          this.selectedStation(),
          this.pigeonMos(),
          this.dtgs(),
          this.dtgIndex(),
          this.selectedTime() - this.startHour
        ),
      });
      this.saveSession();
      this.setRouteStartFlag();
    }, 200);
  }

  setRoute(route): void {
    this.clearSessionStorage('pigeons-mos');
    this.routePosition.set({});
    this.pigeonMos.set({});
    this.pigeonRouteDetails.set({ ...this.newPigeonRoute() });
    if (this.selectedDay() !== null) {
      this.dtgIndex.set(3);
      this.mapsTime.set(this.sliderDtgs[this.dtgIndex()]?.clone());
      this.selectedTime.set(this.startHour + 3);
    }
    if (route < 9999) {
      this.pigeonRoute.set({ ...this.routes()[route] });
      setTimeout(() => {
        this.getPigeonRouteDetails();
        this.setStationsTime(0);
      }, 200);
    }
  }

  onChangeRoute(event: MatSelectChange): void {
    // clear session storage
    const route = event.value;
    if (route < 9999) {
      this.setRoute(route);
    }
    if (this.showArchive() && this.days().length > 0) {
      this.onSelectDay(this.days()[0]);
    } else {
      this.onSelectDay(null);
    }
  }

  onParameterSelect(parameter: string): void {
    this.selectedParameter.set(parameter);
    this.routePosition.update(routePosition => {
      return {
        ...routePosition,
        ['color']: this.pigeonsService.pigeonColor(
          this.parameters[this.selectedParameter()].name,
          routePosition['station'],
          this.pigeonMos(),
          this.dtgs(),
          this.dtgIndex(),
          this.selectedTime() - this.startHour
        ),
      };
    });

    this.setRouteStartFlag();
  }

  initIndividualRouteSearch(): void {
    this.individualRouteForm = this.fb.group({
      individualRouteStart: [null],
      individualRouteEnd: [null],
    });

    this.startStations$ = this.individualRouteForm
      .get('individualRouteStart')
      .valueChanges.pipe(
        debounceTime(100),
        tap(() => this.isStationSearchLoading.set(true)),
        switchMap(input =>
          this.pigeonsService.findStartByName(input).pipe(
            map(result => {
              return result.sort((a, b) => (a.name > b.name ? 1 : -1));
            }),
            finalize(() => {
              this.isStationSearchLoading.set(false);
              this.useStandardRoute = false;
            })
          )
        )
      );

    this.endStations$ = this.individualRouteForm
      .get('individualRouteEnd')
      .valueChanges.pipe(
        debounceTime(1000),
        tap(() => this.isStationSearchLoading.set(true)),
        switchMap(input =>
          this.pigeonsService.findDestinationByName(input).pipe(
            map(result => {
              return result.sort((a, b) => (a.name > b.name ? 1 : -1));
            }),
            finalize(() => {
              this.isStationSearchLoading.set(false);
              this.useStandardRoute = false;
            })
          )
        )
      );
  }

  pigeonPosition(stationId): LngLatLike {
    if (stationId) {
      const matches = this.pigeonRouteDetails().stations.filter(
        s => s.stationId == stationId
      );
      if (matches.length > 0) {
        return matches[0].lnglat;
      }
    }
    return [0, 0];
  }

  setStationsTime(stationIndex: number): void {
    const details = this.pigeonRouteDetails();
    const stations = details.stations;

    const selectedDay = this.selectedDay();
    const selectedTime = this.selectedTime();

    const byStationId: Record<string, number> = {};
    const byIndex: Record<number, number> = {};

    let outOfScope = false;

    for (let i = 0; i < stations.length; i++) {
      const stationId = stations[i].stationId;

      let time: number;

      if (isNil(selectedDay)) {
        time = selectedTime;
      } else {
        const difference = i - stationIndex;
        time = selectedTime + difference;
        if (time < this.startHour || time > this.endHour) {
          outOfScope = true;
        }
      }

      byStationId[stationId] = time;
      byIndex[i] = time;
    }

    this.stationTime.set(byStationId);
    this.stationIndexTime.set(byIndex);
    this.outOfTimeScope.set(outOfScope);
  }

  routeIndexFromCache(pigeonSession): number {
    let routeIndex = 9999;
    if (pigeonSession.routeIndex < this.routes().length) {
      const route = this.routes()[pigeonSession.routeIndex];
      if (
        pigeonSession.startName === route.startName &&
        pigeonSession.endName === route.endName
      ) {
        routeIndex = Number(pigeonSession.routeIndex);
      }
    }
    return routeIndex;
  }

  loadSession(): void {
    this.pigeonRouteDetails.set({ ...this.newPigeonRoute() });
    let pigeonSession;
    const sessionData = sessionStorage.getItem('pigeon-session');
    if (sessionData) {
      pigeonSession = JSON.parse(sessionData);
    } else {
      const userProfile = this.auth.getUserProfile();
      let global = userProfile['metadata']['global'];
      if (!global) {
        global = {};
      }
      if (global.hasOwnProperty('pigeonSession')) {
        pigeonSession = global['pigeonSession'];
      }
    }
    if (pigeonSession) {
      this.routePosition.set({});
      this.pigeonSpeed.set(pigeonSession.speed);
      this.selectedRoute.set(this.routeIndexFromCache(pigeonSession));
      if (this.selectedRoute() > 9998) {
        this.pigeonRoute.set({ ...pigeonSession.route });
        this.individualRouteForm.controls['individualRouteStart'].patchValue(
          pigeonSession.startName,
          { onlySelf: true, emitEvent: false }
        );
        this.individualRouteForm.controls['individualRouteEnd'].patchValue(
          pigeonSession.endName,
          { onlySelf: true, emitEvent: false }
        );
      } else {
        this.pigeonRoute.set({ ...this.routes()[this.selectedRoute()] });
        this.individualRouteForm.controls['individualRouteStart'].patchValue(
          pigeonSession.startName,
          { onlySelf: true, emitEvent: false }
        );
        this.individualRouteForm.controls['individualRouteEnd'].patchValue(
          pigeonSession.endName,
          { onlySelf: true, emitEvent: false }
        );
      }
      this.pigeonRouteDetails.set({
        ...pigeonSession.route,
        geoJsonFlags: this.setGeoJsonFlags(pigeonSession.route),
      });
      this.setStationsTime(0);
      this.selectedStationIndex.set(0);
      this.selectedStation.set(this.pigeonRouteDetails().stations[0].stationId);
      for (const station of this.pigeonRouteDetails().stations) {
        this.pigeonsService
          .mosDataOfStation(station.stationId, this.selectedArchiveDate())
          .then(mosData => {
            if (mosData) {
              this.pigeonMos.update(pigeonMos => {
                return { ...pigeonMos, [station.stationId]: mosData };
              });
              if (station.stationId === this.selectedStation()) {
                this.routePosition.set({
                  station: this.selectedStation(),
                  lnglat: [
                    this.pigeonRouteDetails().points[0].to.lng,
                    this.pigeonRouteDetails().points[0].to.lat,
                  ],
                  color: this.pigeonsService.pigeonColor(
                    this.parameters[this.selectedParameter()].name,
                    this.selectedStation(),
                    this.pigeonMos(),
                    this.dtgs(),
                    this.dtgIndex(),
                    this.selectedTime() - this.startHour
                  ),
                });
              }
            }
          });
      }
    } else {
      this.selectedRoute.set(0);
      this.selectStandardRoute(this.selectedRoute());
    }
  }

  saveSession(): void {
    const pigeonSession = {
      speed: this.pigeonSpeed(),
      route: this.pigeonRouteDetails(),
      routeIndex: this.selectedRoute(),
      startName:
        this.individualRouteForm.controls['individualRouteStart'].value,
      endName: this.individualRouteForm.controls['individualRouteEnd'].value,
    };
    sessionStorage.setItem('pigeon-session', JSON.stringify(pigeonSession));
    this.saveUserSettings(pigeonSession);
    this.storePigeonRoute();
  }

  storePigeonRoute(): void {
    localStorage.setItem(
      'pigeon-route',
      JSON.stringify(this.pigeonRouteDetails())
    );
  }

  saveUserSettings(pigeonSession): void {
    const userProfile = this.auth.getUserProfile();
    if (userProfile) {
      let global = userProfile['metadata']['global'];
      if (!global) {
        global = {};
      }
      global['pigeonSession'] = pigeonSession;
      this.auth.updateUserData('global', global);
    }
  }

  onChangeStation(event): void {
    const station = event.value;
    this.selectedStationIndex.set(
      this.pigeonRouteDetails().stations.findIndex(s => s.stationId === station)
    );
    this.selectedTime.set(this.stationTime()[event.value]);
    this.setStationsTime(this.selectedStationIndex());
    this.routePosition.set({
      station,
      lnglat: [
        this.pigeonRouteDetails().points[this.selectedStationIndex()].to.lng,
        this.pigeonRouteDetails().points[this.selectedStationIndex()].to.lat,
      ],
      color: this.pigeonsService.pigeonColor(
        this.parameters[this.selectedParameter()].name,
        station,
        this.pigeonMos(),
        this.dtgs(),
        this.dtgIndex(),
        this.selectedTime() - this.startHour
      ),
    });
  }

  onMatrixSelectionChange(event): void {
    const hour = event.hour;
    const station = event.station;
    const stationPos = event.stationIndex;
    this.selectedStation.set(station);
    this.selectedTime.set(hour);
    this.selectedStationIndex.set(stationPos);
    const currentDay =
      this.selectedDay() === null ? moment() : moment(this.selectedDay());
    const targetDtg = moment(currentDay).set('hour', 0).add(hour, 'hours');
    for (let i = 0; i < this.dtgs().length; i++) {
      const dtg = moment.unix(this.dtgs()[i].slider);
      if (dtg.isSame(targetDtg, 'hour')) {
        this.dtgIndex.set(i);
        break;
      }
    }
    // const stationPos = this.stationPositionOfRoute(station);
    this.routePosition.set({
      station,
      lnglat: [
        this.pigeonRouteDetails().points[stationPos].to.lng,
        this.pigeonRouteDetails().points[stationPos].to.lat,
      ],
      color: this.pigeonsService.pigeonColor(
        this.parameters[this.selectedParameter()].name,
        station,
        this.pigeonMos(),
        this.dtgs(),
        this.dtgIndex(),
        hour - this.startHour
      ),
    });
    this.setStationsTime(stationPos);
    this.setRouteStartFlag();
  }

  onMapPositionChange(newPosition): void {
    if (newPosition.posType === 'pos') {
      this.routePosition.set({});
      const station =
        this.pigeonRouteDetails().stations[newPosition.position].stationId;
      setTimeout(() => {
        this.routePosition.set({
          station,
          lnglat: [
            this.pigeonRouteDetails().points[newPosition.position].to.lng,
            this.pigeonRouteDetails().points[newPosition.position].to.lat,
          ],
          color: this.pigeonsService.pigeonColor(
            this.parameters[this.selectedParameter()].name,
            station,
            this.pigeonMos(),
            this.dtgs(),
            this.dtgIndex(),
            this.selectedTime() - this.startHour
          ),
        });
        this.selectedTime.set(this.stationTime()[station]);
        this.mapsTime.set(this.dtgOfSelectedTimeStep()?.clone());
        this.selectedStation.set(station);
        this.selectedStationIndex.set(newPosition.position);
        this.setStationsTime(newPosition.position);
        for (let i = 0; i < this.dtgs().length; i++) {
          const dtg = moment.unix(this.dtgs()[i].slider);
          if (dtg.isSame(this.mapsTime())) {
            this.dtgIndex.set(i);
            break;
          }
        }
      }, 10);
    }
  }

  onMapStartChange(event): void {
    this.startChangeFromMap = true;
    this.selectedRoute.set(9999);
    const end = {
      name: this.individualRouteForm.controls['individualRouteEnd'].value,
      longitude: this.pigeonRouteDetails().end.lng,
      latitude: this.pigeonRouteDetails().end.lat,
    };
    this.useStandardRoute = true;
    this.individualRouteForm.controls['individualRouteStart'].patchValue(
      event.name,
      { onlySelf: true, emitEvent: false }
    );

    this.createPigeonRoute(event, end);
    this.startChangeFromMap = false;
  }

  onMapEndChange(event): void {
    this.startChangeFromMap = true;
    this.selectedRoute.set(9999);
    const start = {
      name: this.individualRouteForm.controls['individualRouteStart'].value,
      longitude: this.pigeonRouteDetails().start.lng,
      latitude: this.pigeonRouteDetails().start.lat,
    };
    this.useStandardRoute = true;
    this.individualRouteForm.controls['individualRouteEnd'].patchValue(
      event.name,
      { onlySelf: true, emitEvent: false }
    );

    this.createPigeonRoute(start, event);
    this.startChangeFromMap = false;
  }

  onMapHourChange(hour: number): void {
    this.selectedTime.set(hour);
    this.mapsTime.set(moment(this.mapsTime()).set({ hour })?.clone());
    for (let i = 0; i < this.dtgs().length; i++) {
      const dtg = moment.unix(this.dtgs()[i].slider);
      if (dtg.isSame(this.mapsTime())) {
        this.dtgIndex.set(i);
        break;
      }
    }

    for (const x in this.stationIndexTime()) {
      if (this.stationIndexTime().hasOwnProperty(x)) {
        const timeIndex = Number(x);
        if (this.stationIndexTime()[timeIndex] === hour) {
          this.selectedStationIndex.set(timeIndex);
          this.selectedStation.set(
            this.pigeonRouteDetails().stations[timeIndex].stationId
          );
          break;
        }
      }
    }
    this.routePosition.set({
      station: this.selectedStation(),
      lnglat: [
        this.pigeonRouteDetails().points[this.selectedStationIndex()].to.lng,
        this.pigeonRouteDetails().points[this.selectedStationIndex()].to.lat,
      ],
      color: this.pigeonsService.pigeonColor(
        this.parameters[this.selectedParameter()].name,
        this.selectedStation(),
        this.pigeonMos(),
        this.dtgs(),
        this.dtgIndex(),
        hour - this.startHour
      ),
    });

    // this.setStationsTime(this.selectedStationIndex());
  }

  onStartStationSelect(value: FilteredStations): void {
    // Code for when startpoint for individual route is selected
    // value = whole station object
    this.selectedRoute.set(9999);
    const end = {
      name: this.individualRouteForm.controls['individualRouteEnd'].value,
      longitude: this.pigeonRouteDetails().end.lng,
      latitude: this.pigeonRouteDetails().end.lat,
    };
    this.createPigeonRoute(value, end);
  }

  onEndStationSelect(value: FilteredStations): void {
    // Code for when endpoint for individual route is selected
    // value = whole station object
    this.selectedRoute.set(9999);
    const start = {
      name: this.individualRouteForm.controls['individualRouteStart'].value,
      longitude: this.pigeonRouteDetails().start.lng,
      latitude: this.pigeonRouteDetails().start.lat,
    };
    this.createPigeonRoute(start, value);
  }

  onToggleTutorialVideo(): void {
    this.showTutorialVideo.update(showTutorialVideo => {
      return !showTutorialVideo;
    });
    if (this.showTutorialVideo()) {
      this.onClickScroll('top');
    }
  }

  onClickScroll(elementId: string): void {
    this.viewportScroller.scrollToAnchor(elementId);
  }

  onOpenDrawer(contentToShow: string): void {
    this.hideSettings.update(hideSettings => !hideSettings);
    this.drawer().toggle();
  }

  onCloseTutorialVideo(): void {
    this.showTutorialVideo.set(false);
  }

  public onChangeNeutralColor(color: string): void {
    if (color !== this.neutralColor()) {
      this.neutralColor.set(color);
      this.eppService.setGreenTableDesign(this.neutralColor() === 'green');
      this.smwService.greenTableDesign.set(this.neutralColor() === 'green');
      localStorage.setItem('eopTableColorScheme', color);
      const userProfile = this.auth.getUserProfile();
      if (userProfile) {
        let global = userProfile['metadata']['global'];
        if (!global) {
          global = {};
        }
        global['eopTableColorScheme'] = this.neutralColor();
        this.auth.updateUserData('global', global);
      }
    }
  }

  onSetupTableColumns(parameterset: string): void {
    this.settingsParameterTable.set([
      ...(this.pigeonsService.userParametersets[parameterset] ?? []),
    ]);
    this.settingsParameterset.set(parameterset);
    this.showTableSettings.set(true);
  }

  onRestoreStandardTableSettings(): void {
    this.settingsParameterTable.set([
      ...(this.pigeonsService.toUserParameterset(
        this.pigeonsService.defaultParametersetDetails[
          this.settingsParameterset()
        ]
      ) ?? []),
    ]);
  }

  onCancelTableSettings(): void {
    this.showTableSettings.set(false);
  }

  onSaveTableSettings(): void {
    this.pigeonsService.saveTableSettings(
      this.settingsParameterTable(),
      this.neutralColor()
    );
    this.showTableSettings.set(false);
  }

  onDrop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );
  }

  onScrollToCard(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  onToggleColor(isGreen: boolean) {
    const newColor = isGreen ? 'green' : 'white';
    this.onChangeNeutralColor(newColor);
  }

  onGetEppDtg(event: Moment): void {
    this.eppForecastDtg.set(event?.clone());
  }

  // =================================== Functions For Setting The Start-Flag ============================================================
  // (different colors depending on mos index of chosen parameter)

  async setRouteStartFlag(): Promise<void> {
    const parameterValueOfFirstStationInRoute =
      await this.getParameterValueOfFirstStationInRoute();

    this.routeStartFlag = 'flag-' + parameterValueOfFirstStationInRoute;

    this.pigeonRouteDetails.update(pigeonRouteDetails => {
      const geoJsonFlags = this.setGeoJsonFlags(pigeonRouteDetails);
      pigeonRouteDetails.geoJsonFlags = geoJsonFlags;
      return { ...pigeonRouteDetails };
    });
  }

  async getParameterValueOfFirstStationInRoute(): Promise<number | string> {
    const idOfFirstStationInRoute = this.getIdOfFirstStationInRoute();
    if (idOfFirstStationInRoute === null) {
      return 'x';
    } // x --> index for grey flag

    const startTimeOfFirstStationInRoute =
      this.getStartTimeOfFirstStationInRoute(idOfFirstStationInRoute);
    if (startTimeOfFirstStationInRoute === null) {
      return 'x';
    }

    // check if start time is out of time range the pigeons are flying (between this.startHour & this.endHour)
    if (startTimeOfFirstStationInRoute < this.startHour) {
      return 'x';
    }

    // delivers the index of the dataset with the idOfFirstStationInRoute & in the correct UTC time from this.pigeonsService.mosData
    // the index is then used to get the right data set from pigeonMos
    const mosIndex = await this.getMosIndex(
      idOfFirstStationInRoute,
      startTimeOfFirstStationInRoute,
      this.selectedDay()
    );

    // returns single mosData of firstStationOfRoute with the correct time in UTC
    const dataSet = this.getDataSetWithMosIndexFromMosData(
      idOfFirstStationInRoute,
      mosIndex
    );

    const parameterName = this.getClearNameOfParameter(
      dataSet,
      this.selectedParameter()
    );

    return this.getParameterValueFromDataSet(dataSet, parameterName);
  }

  // Helper functions for Setting The Start-Flag

  getIdOfFirstStationInRoute(): number | null {
    if (this.pigeonRouteDetails()['stations'].length > 0) {
      return this.pigeonRouteDetails()['stations'][0]['stationId'];
    } else {
      return null;
    }
  }

  getStartTimeOfFirstStationInRoute(
    idOfFirstStationInRoute: number
  ): number | null {
    if (this.stationTime().hasOwnProperty(idOfFirstStationInRoute)) {
      return this.stationTime()[idOfFirstStationInRoute];
    } else {
      return null;
    }
  }

  getMosIndex(
    idOfFirstStationInRoute: number,
    startTimeOfFirstStationInRoute: number,
    selectedDay: Moment
  ): Promise<any> {
    /*needed because this.pigeonsService.mosIndex() always returns 0 because mosData isn't available immediately
    --> so wait till this.pigeonsService.mosData has idOfFirstStationInRoute*/
    return new Promise<CollectionOfPigeonMosData>((resolve, reject) => {
      const neededPigeonMosData = setInterval(() => {
        if (
          this.pigeonsService.mosData().hasOwnProperty(idOfFirstStationInRoute)
        ) {
          clearInterval(neededPigeonMosData);
          const mosIndex = this.pigeonsService.mosIndex(
            idOfFirstStationInRoute,
            startTimeOfFirstStationInRoute,
            selectedDay
          );
          resolve(mosIndex);
        }
      }, 100);
    });
  }

  getDataSetWithMosIndexFromMosData(
    idOfFirstStationInRoute: number,
    mosIndex: number
  ): SinglePigeonMosData {
    if (
      this.pigeonMos().hasOwnProperty(idOfFirstStationInRoute) &&
      this.pigeonMos()[idOfFirstStationInRoute].hasOwnProperty(mosIndex)
    ) {
      return this.pigeonMos()[idOfFirstStationInRoute][mosIndex];
    }
  }

  getClearNameOfParameter(
    singlePigeonMosData: SinglePigeonMosData,
    selectedParameter: string
  ): string {
    // get clearname of parameter as defined in parameter-settings.ts (.e.g main --> pigeonIndex)
    let clearNameOfParameter = '';
    if (this.pigeonsService.parameterInfo.hasOwnProperty(selectedParameter)) {
      clearNameOfParameter =
        this.pigeonsService.parameterInfo[selectedParameter].name;
    }
    // in case clearNameOfParameter in parameter-settings.ts is defined of two names (e.g. TTT --> coldIndex|heatIndex )
    if (clearNameOfParameter.indexOf('|') > 0) {
      const parameters = clearNameOfParameter.split('|');
      const valueParameter1 = singlePigeonMosData[parameters[0]];
      const valueParameter2 = singlePigeonMosData[parameters[1]];
      clearNameOfParameter =
        valueParameter1 > valueParameter2 ? parameters[0] : parameters[1];
    }
    return clearNameOfParameter;
  }

  getParameterValueFromDataSet(
    dataSet: SinglePigeonMosData,
    parameterName: string
  ): number | string {
    if (dataSet && dataSet.hasOwnProperty(parameterName)) {
      return dataSet[parameterName];
    }
    return 'x';
  }

  onArchiveDateChange(event: MatDatepickerInputEvent<Moment>) {
    const modelRun = moment(event.value).add(6, 'hours');
    this.archiveDateControl.setValue(modelRun);
    this.selectedArchiveDate.set(moment(modelRun).utc().set('hour', 0));
    this.onSelectDay(null);
    this.setDtgs();
    this.getNewMosData(modelRun);
  }

  getNewMosData(modelRun: Moment | null = null) {
    this.clearSessionStorage('pigeons-mos');
    this.pigeonMos.set({});
    this.pigeonsService.mosData.set({});
    let i = 0;
    for (const station of this.pigeonRouteDetails().stations) {
      this.pigeonsService
        .mosDataOfStation(station.stationId, modelRun)
        .then(mosData => {
          if (mosData) {
            this.pigeonMos.update(pigeonMos => {
              return { ...pigeonMos, [station.stationId]: mosData };
            });
            if (i === 0) {
              this.selectedStation.set(station.stationId);
              this.selectedStationIndex.set(0);
              this.routePosition.set({
                station: this.selectedStation(),
                lnglat: [
                  this.pigeonRouteDetails().points[0].to.lng,
                  this.pigeonRouteDetails().points[0].to.lat,
                ],
                color: this.pigeonsService.pigeonColor(
                  this.parameters[this.selectedParameter()].name,
                  this.selectedStation(),
                  this.pigeonMos(),
                  this.dtgs(),
                  this.dtgIndex(),
                  this.selectedTime() - this.startHour
                ),
              });
            }
          }
          i++;
        });
    }
  }

  private listenToFillSunTimesArray() {
    effect(
      () => {
        const selectedStation = this.selectedStation();
        const selectedDay = this.selectedDay();
        const selectedStationIndex = this.selectedStationIndex();
        const pigeonRouteDetails = this.pigeonRouteDetails();

        untracked(() => {
          if (!isNil(selectedStation) && !isNil(selectedDay)) {
            const dayOfYear = selectedDay.dayOfYear();
            if (isNil(this.sunTimesArray()?.[selectedStation])) {
              this.sunTimesArray.update(sunTimesArray => {
                return { ...sunTimesArray, [selectedStation]: {} };
              });
            }
            if (
              !isNil(selectedStationIndex) &&
              !isNil(pigeonRouteDetails.stations[selectedStationIndex])
            ) {
              const station = pigeonRouteDetails.stations[selectedStationIndex];
              const sunriseset = this.pigeonsService.getSunTimes(
                station.lnglat[1],
                station.lnglat[0],
                selectedDay
              );
              this.sunTimesArray.update(sunTimesArray => {
                return {
                  ...sunTimesArray,
                  [selectedStation]: {
                    ...sunTimesArray[selectedStation],
                    [dayOfYear]: sunriseset,
                  },
                };
              });
            }
          }
        });
      },
      { injector: this.injector }
    );
  }
  // =====================================================================================================================================
}
